import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Clock, User, ArrowLeft } from "lucide-react";

export default function AssignmentSubmissions() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get("assignment_id");
  const assignmentTitle = searchParams.get("title") || "Assignment";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (assignmentId) {
      loadSubmissions();
    }
  }, [assignmentId]);

  const loadSubmissions = async () => {
    if (!assignmentId) return;

    setLoading(true);
    setError("");
    try {
      const result = await api.getAssignmentSubmissions(assignmentId);
      setData(result);
    } catch (err: any) {
      console.error("Failed to load submissions:", err);
      setError(err.message || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      not_started: {
        label: "Not Started",
        className: "bg-gray-100 text-gray-700",
      },
      in_progress: {
        label: "In Progress",
        className: "bg-blue-100 text-blue-700",
      },
      submitted: {
        label: "Submitted",
        className: "bg-green-100 text-green-700",
      },
      graded: { label: "Graded", className: "bg-purple-100 text-purple-700" },
    };

    const config = statusConfig[status] || statusConfig.not_started;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Assignments
        </Button>
        <h1 className="text-3xl font-bold">{assignmentTitle}</h1>
        <p className="text-gray-600 mt-1">Student Submissions</p>
      </div>

      {loading && (
        <div className="py-8 text-center text-gray-500">
          Loading submissions...
        </div>
      )}

      {error && (
        <div className="py-4 px-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-6">
          {error}
        </div>
      )}

      {!loading && data && (
        <div className="space-y-6">
          {/* Assignment Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600">Total Students</p>
                <p className="text-3xl font-bold">{data.submissions.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600">Submitted</p>
                <p className="text-3xl font-bold text-green-600">
                  {
                    data.submissions.filter((s: any) =>
                      ["submitted", "graded"].includes(s.status),
                    ).length
                  }
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-3xl font-bold text-blue-600">
                  {
                    data.submissions.filter(
                      (s: any) => s.status === "in_progress",
                    ).length
                  }
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600">Not Started</p>
                <p className="text-3xl font-bold text-gray-600">
                  {
                    data.submissions.filter(
                      (s: any) => s.status === "not_started",
                    ).length
                  }
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Requirements */}
          {data.assignment.content && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <strong>Requirements:</strong> Complete{" "}
              {data.assignment.content.min_problems || 20} problems with{" "}
              {data.assignment.content.min_accuracy || 80}% accuracy per
              technique
            </div>
          )}

          {/* Student Submissions */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Student Progress</h2>
            {data.submissions.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No students enrolled in this classroom yet
                </CardContent>
              </Card>
            ) : (
              data.submissions.map((submission: any) => (
                <Card key={submission.student_id} className="overflow-hidden">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-white border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">
                            {submission.student_first_name
                              ? `${submission.student_first_name} ${submission.student_last_name}`.trim()
                              : submission.student_username}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {submission.student_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(submission.status)}
                        {submission.score !== null &&
                          submission.score !== undefined && (
                            <div className="text-right">
                              <div className="text-2xl font-bold">
                                {submission.score}%
                              </div>
                              <div className="text-xs text-gray-500">Score</div>
                            </div>
                          )}
                      </div>
                    </div>

                    {submission.problems_attempted > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Problems:</span>{" "}
                          <span className="font-semibold">
                            {submission.problems_attempted}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Correct:</span>{" "}
                          <span className="font-semibold">
                            {submission.problems_correct}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Accuracy:</span>{" "}
                          <span className="font-semibold">
                            {submission.overall_accuracy.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-4">
                    {submission.technique_breakdown.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        No practice sessions yet
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">
                          Technique Progress
                        </h4>
                        {submission.technique_breakdown.map(
                          (technique: any) => {
                            const minProblems =
                              data.assignment.content?.min_problems || 20;
                            const progressPercent = Math.min(
                              (technique.problems_attempted / minProblems) *
                                100,
                              100,
                            );

                            return (
                              <div
                                key={technique.technique_id}
                                className="p-3 bg-gray-50 rounded-lg"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">
                                        {technique.technique_name}
                                      </span>
                                      {technique.meets_requirements && (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right text-xs">
                                    <div>
                                      {technique.problems_attempted}/
                                      {minProblems} problems
                                    </div>
                                    <div className="text-gray-600">
                                      {technique.accuracy.toFixed(1)}% accuracy
                                    </div>
                                  </div>
                                </div>
                                <Progress
                                  value={progressPercent}
                                  className="h-2"
                                />
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}

                    {submission.submitted_at && (
                      <div className="mt-4 pt-3 border-t flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        Submitted:{" "}
                        {new Date(submission.submitted_at).toLocaleString()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
